import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '@client/components/hooks/use-toast';
import { selectSessionAdvisor, selectAdvisorHierarchyState } from '@client/model/selectors';
import {
  deleteAdvisorHierarchyRelationshipByAdvisorIdRequest,
  fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest,
} from '@client/model/advisorHierarchyModel';

/**
 * Hook for managing leaving a supervisor's team
 * Handles leave confirmation dialog, mutation lifecycle, and toast notifications
 */
export function useAdvisorHierarchyLeave() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const { deleteMutationStatus, deleteMutationError } = useSelector(selectAdvisorHierarchyState);

  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [relationshipToLeave, setRelationshipToLeave] =
    useState<SupervisorAdvisorRelationship | null>(null);
  const prevLeavingRef = useRef(false);
  // Track whether this hook initiated the leave operation
  const initiatedLeaveRef = useRef(false);

  // Handle leave mutation completion
  useEffect(() => {
    const isLeaving = deleteMutationStatus === 'loading';

    // Mutation just completed (was leaving, now not) and this hook initiated it
    if (prevLeavingRef.current && !isLeaving && initiatedLeaveRef.current) {
      initiatedLeaveRef.current = false;
      if (deleteMutationError) {
        toast({
          title: 'Error',
          description: deleteMutationError || 'Failed to leave team. Please try again.',
          variant: 'destructive',
          className: '-translate-y-5',
        });
      } else if (deleteMutationStatus === 'success') {
        toast({
          title: 'Success',
          description: 'You have left the team successfully.',
          className: '-translate-y-5',
        });
      }
    }

    prevLeavingRef.current = isLeaving;
  }, [deleteMutationStatus, deleteMutationError, dispatch, sessionAdvisor?.advisorId, toast]);

  const handleLeave = (relationship: SupervisorAdvisorRelationship) => {
    setRelationshipToLeave(relationship);
    setLeaveDialogOpen(true);
  };

  const confirmLeave = () => {
    if (!relationshipToLeave || !sessionAdvisor?.advisorId) {
      return;
    }

    if (!relationshipToLeave.advisorId) {
      toast({
        title: 'Error',
        description: 'Unable to leave team. Missing supervisor information.',
        variant: 'destructive',
        className: '-translate-y-5',
      });
      return;
    }

    initiatedLeaveRef.current = true;
    dispatch(
      deleteAdvisorHierarchyRelationshipByAdvisorIdRequest({
        advisorId: sessionAdvisor.advisorId,
        supervisorAdvisorId: relationshipToLeave.advisorId,
      }),
    );

    setLeaveDialogOpen(false);
    setRelationshipToLeave(null);
  };

  const cancelLeave = () => {
    setLeaveDialogOpen(false);
    setRelationshipToLeave(null);
  };

  return {
    // Dialog state
    leaveDialogOpen,
    relationshipToLeave,

    // Mutation state
    isLeaving: deleteMutationStatus === 'loading',

    // Actions
    handleLeave,
    confirmLeave,
    cancelLeave,
  };
}

export type UseAdvisorHierarchyLeaveReturn = ReturnType<typeof useAdvisorHierarchyLeave>;

